import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Sheet, SheetContent } from './ui/sheet';
import {
  LayoutDashboard, BedDouble, Tag, CalendarDays,
  BarChart3, Globe2, LogOut, Menu, Hotel, ChevronRight, Percent, Grid3x3, BadgePercent
} from 'lucide-react';

const navItems = [
  { key: 'dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { key: 'reservations', href: '/admin/reservations', icon: BedDouble },
  { key: 'rooms', href: '/admin/rooms', icon: Hotel },
  { key: 'pricing', href: '/admin/pricing', icon: Tag },
  { key: 'allotments', href: '/admin/allotments', icon: Grid3x3 },
  { key: 'discounts', href: '/admin/discounts', icon: BadgePercent },
  { key: 'vat_settings', href: '/admin/vat-settings', icon: Percent },
  { key: 'calendar', href: '/admin/calendar', icon: CalendarDays },
  { key: 'reports', href: '/admin/reports', icon: BarChart3 },
  { key: 'channels', href: '/admin/channels', icon: Globe2 },
];

function SidebarContent({ onClose }) {
  const { t, adminUser, adminLogout, language, setLanguage } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
    if (onClose) onClose();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1F3A2E' }}>
      {/* Logo */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '18px', fontWeight: 600, color: 'white', marginBottom: '2px' }}>
          Amorium Hotel
        </p>
        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)' }}>
          {language === 'tr' ? 'Yönetim Paneli' : 'Management Panel'}
        </p>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }} data-testid="admin-sidebar-nav">
        {navItems.map(({ key, href, icon: Icon }) => {
          const active = location.pathname === href;
          return (
            <Link
              key={key}
              to={href}
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: '10px',
                marginBottom: '2px', textDecoration: 'none', fontSize: '13px', fontWeight: 500,
                background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: active ? 'white' : 'rgba(255,255,255,0.65)',
                borderLeft: active ? '2.5px solid #B08D57' : '2.5px solid transparent',
                transition: 'background 0.15s, color 0.15s',
                fontFamily: 'Manrope, sans-serif',
              }}
              onMouseEnter={e => !active && (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseLeave={e => !active && (e.currentTarget.style.background = 'transparent')}
            >
              <Icon size={17} style={{ color: active ? '#B08D57' : 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
              {t(key)}
              {active && <ChevronRight size={13} style={{ marginLeft: 'auto', color: '#B08D57' }} />}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#B08D57', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
            {adminUser?.name?.charAt(0) || 'A'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: 'white', fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {adminUser?.name || 'Admin'}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {adminUser?.email}
            </p>
          </div>
        </div>
        <button
          onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')}
          style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', background: 'transparent', border: 'none', cursor: 'pointer', marginBottom: '8px', display: 'block', fontFamily: 'Manrope, sans-serif' }}
        >
          🌐 {language === 'tr' ? 'TR → EN' : 'EN → TR'}
        </button>
        <button
          onClick={handleLogout}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'white'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        >
          <LogOut size={14} />
          {t('logout')}
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'Manrope, sans-serif' }}>
      {/* Desktop Sidebar */}
      {isDesktop && (
        <aside style={{ width: '240px', flexShrink: 0, position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 40, overflowY: 'auto' }}>
          <SidebarContent />
        </aside>
      )}

      {/* Mobile Sheet */}
      {!isDesktop && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" style={{ padding: 0, width: '240px', background: '#1F3A2E', border: 'none' }}>
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, marginLeft: isDesktop ? '240px' : 0, minHeight: '100vh', background: '#F6F1E7', display: 'flex', flexDirection: 'column' }}>
        {/* Mobile Topbar */}
        {!isDesktop && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', height: '56px', background: '#1F3A2E', color: 'white' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '17px', fontWeight: 600 }}>Amorium Hotel</p>
            <button
              onClick={() => setMobileOpen(true)}
              style={{ padding: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'white' }}
            >
              <Menu size={20} />
            </button>
          </div>
        )}
        {/* Page Content */}
        <main style={{ flex: 1, padding: '24px 32px' }}>
          {children}
        </main>
      </div>
    </div>
  );
}
